import { useEffect } from 'react';

import { BranchSwitch } from './ui/BranchSwitch';
import { Info } from './ui/Info';
import { LabProvider, useLab } from './ui/LabContext';
import { ScenarioPanel } from './ui/ScenarioPanel';
import { SelectionModule } from './ui/SelectionModule';
import { ZoomControls } from './ui/ZoomControls';
import { EvolutionChart } from './ui/charts/EvolutionChart';
import { InstantThreatChart } from './ui/charts/InstantThreatChart';
import { PassiveThreatChart } from './ui/charts/PassiveThreatChart';
import { RatioChart } from './ui/charts/RatioChart';
import { StrengthChart } from './ui/charts/StrengthChart';
import { UnderstandingEvolution } from './ui/pages/UnderstandingEvolution';
import { UnderstandingThreat } from './ui/pages/UnderstandingThreat';
import { PAGES, RouterProvider, useRouter } from './ui/router';

function PageNav() {
  const { page, navigate } = useRouter();
  return (
    <nav className="page-nav" aria-label="Pages">
      {PAGES.map((entry) => (
        <button
          key={entry.id}
          type="button"
          className={`page-tab${page === entry.id ? ' active' : ''}`}
          aria-current={page === entry.id ? 'page' : undefined}
          onClick={() => navigate(entry.id)}
        >
          {entry.label}
        </button>
      ))}
    </nav>
  );
}

function ChartsPage() {
  const { tuning } = useLab();
  return (
    <>
      {/* The right rail has to hold number fields as well as readouts while
          tuning, so the row widens for them rather than the fields shrinking. */}
      <div className={`charts-panel${tuning ? ' tuning' : ''}`}>
        <ZoomControls />
        <EvolutionChart />
        <InstantThreatChart />
        <PassiveThreatChart />
        {/* Row 4 is whichever question the page is being used to answer:
            what the curves produce, or what they are being tuned against. */}
        {tuning ? <RatioChart /> : <StrengthChart />}
      </div>
      {/* Below the charts, because it is a summary of them: every knob is now in
          a rail, and what is left here is what the set adds up to. Reset is the
          one part that is not a summary, so it stays in the header. */}
      {tuning ? <ScenarioPanel /> : null}
      <SelectionModule />
    </>
  );
}

/**
 * The tuner's on/off switch, in the header beside the branch switch.
 *
 * Present even when off, so the feature is discoverable from the page rather
 * than only from a URL someone has to have been given.
 *
 * Reset scenario is not here: it lives at the foot of the last chart's right
 * rail, in the bottom right corner of the chart panel, below the four rails it
 * undoes.
 */
function TuneSwitch() {
  const { tuning, setTuning } = useLab();
  return (
    <label className="tune-switch" title="Add a tunable scenario curve and the ratio chart">
      <input type="checkbox" checked={tuning} onChange={(event) => setTuning(event.target.checked)} />
      <span>Tune</span>
    </label>
  );
}

/**
 * What a prose page needs from the shared state, applied on arrival.
 *
 * Two things, and both follow from the pages being written about *a* branch
 * rather than about a comparison: the selection collapses to the primary, since
 * that is the one the paragraphs are quoting; and the tuner is switched on, so
 * the Scenario is offered here whether or not it was offered on the charts.
 * Leaving it off would make the candidate the one thing the lab cannot explain.
 *
 * Both are ordinary state changes, so the charts page keeps whatever this
 * leaves behind rather than the two views disagreeing about what is selected.
 */
function useSingleBranchPage(prose: boolean) {
  const { tuning, setTuning, rulesetIds, setRulesetIds } = useLab();
  useEffect(() => {
    if (!prose) return;
    if (!tuning) setTuning(true);
    if (rulesetIds.length > 1) setRulesetIds([rulesetIds[0]]);
  }, [prose, tuning, setTuning, rulesetIds, setRulesetIds]);
}

function Shell() {
  const { page } = useRouter();
  const prose = page !== 'charts';
  useSingleBranchPage(prose);
  return (
    <div className="app">
      <header className="app-head">
        <h1>Biter Battles — Mechanics Lab</h1>
        <Info title="What this is">
          <p>
            Not a beginner guide. This is the machinery: how difficulty scales science into
            mutagen, how mutagen buys evolution, how evolution turns into threat, and how threat
            and evolution turn into biters that actually hurt.
          </p>
          <p>
            Every curve is the scenario's own <code>calc_feed_effects</code>, ported line by line
            and pinned against <code>tests/test-feeding.lua</code>. Curves are drawn from sampled
            points, but every number reported — cursor, selection endpoints, deltas — runs the
            real calculation at that exact mutagen value. Nothing on screen is interpolated.
          </p>
          <p>
            <b>Two branches</b> are ported, and the switch in the header moves between them:{' '}
            <code>master</code>, and the <code>cojito_3v3_balance_testing</code> balance test,
            which replaces the feeding loop with a closed form, deletes instant threat and makes
            passive income linear in mutagen. Switching holds the evolution window so the two are
            directly comparable. A third, the <b>Scenario</b>, is the candidate being tuned rather
            than a transcription; it is always available on the prose pages and behind{' '}
            <b>Tune</b> here.
          </p>
          <p>
            <b>The x axis</b> is total raw mutagen fed since evolution 0, in the game's own
            "Mutagen strength" units (<code>food_value × 10000</code>, so one space flask is
            4,375). All four charts share it, which is what lets them share a cursor, a selection
            and a zoom.
          </p>
          <p>
            <b>Dashed vertical guides</b> mark evolution events — first medium biters, first big
            biters, boss units, first behemoths, 100% evolution. They fade out when zoomed far
            enough that they would all crowd into the left edge. The fainter guides sit on the
            evolution ticks of the top axis.
          </p>
          <p>
            Hover a chart to move the shared cursor; the values land in each chart's right rail
            and the position is tagged on the two shared axes. Drag to select a range. Zoom
            controls appear on a chart when you hover it, and the scroll wheel works anywhere.
          </p>
          <p>
            <b>Pages</b> in the header hold the long-form explanations the (i) popovers summarise.
            Written derivations for all of it live in <code>game-math.md</code> at the repository
            root.
          </p>
        </Info>
        <BranchSwitch single={prose} />
        {prose ? null : <TuneSwitch />}
        <PageNav />
      </header>

      {page === 'threat' ? (
        <UnderstandingThreat />
      ) : page === 'evolution' ? (
        <UnderstandingEvolution />
      ) : (
        <ChartsPage />
      )}
    </div>
  );
}

export default function App() {
  return (
    <LabProvider>
      <RouterProvider>
        <Shell />
      </RouterProvider>
    </LabProvider>
  );
}
